/**
 * Simulation state for rules and enemies (GDD §4, §5), preallocated at load so a step creates nothing (GDD §10.5).
 * Field names follow the concept's game layer (concept L866–L1215) so the port reads side by side with it.
 */
import type { EntityType } from '../render/frame.ts';

export type EnemyType = EntityType;
export const ENEMY_TYPES: readonly EnemyType[] = ['ship', 'heli', 'fuel', 'jet'];

/** The nine ways to lose a jet (GDD §4.4); the keys of `config.text.jetLost`. */
export type CrashCause =
  'riverbank' | 'gunboat' | 'heli' | 'jet' | 'bridge' | 'heliFire' | 'flak' | 'bridgeGuns' | 'fuel';

/**
 * Which rules run: 'gdd' in production; 'concept' reproduces the concept's game layer for the parity tests
 * (master plan porting rule 4). The differences are D7, D8, D9→Q2, D11, D14, D17 and D20.
 */
export type Rules = 'concept' | 'gdd';

export interface Enemy {
  type: EnemyType;
  /** Model slot within its type's pool (concept: the pooled model it acquired, L909). */
  slot: number;
  x: number;
  /** Base height: helicopters 13–17 m, enemy jets 14 m, boats 0 (GDD §5). */
  y: number;
  z: number;
  /** Sideways velocity (m/s); a helicopter's vertical velocity while it falls. */
  vx: number;
  vy: number;
  move: boolean;
  /** Sleeper: starts to patrol once the jet is within 250 m (GDD §5.1). */
  near: boolean;
  fire: boolean;
  /** Seconds of death animation left; 0 while alive. */
  dying: number;
  /** Patrol speed (m/s). */
  spd: number;
  /** Hit box half-width (across the river) and half-length (along it), GDD §5. */
  hw: number;
  hl: number;
  /** Fire cooldown (s). */
  cd: number;
  /** +1 heading toward +x, −1 toward −x. */
  face: number;
  yaw: number;
  /** Animation phase. */
  ph: number;
  /** The autopilot's predicted x (scratch, concept L1156). */
  px: number;
  /** Model pose, world metres and Euler radians (order 'YXZ' for helicopters, 'XYZ' otherwise). */
  posX: number;
  posY: number;
  posZ: number;
  rotX: number;
  rotY: number;
  rotZ: number;
  /** Model pose of the previous step, for interpolated drawing. */
  prevX: number;
  prevY: number;
  prevZ: number;
  /** False until the first update poses the model (the concept draws a fresh model at the origin until then). */
  posed: boolean;
  /** Gunboat radar angle and V wake. */
  radar: number;
  wake: boolean;
  /** Beacon (helicopter, barges) or strobe (enemy jet) on. */
  light: boolean;
}

export type ShotKind = 'tracer' | 'flak';

export interface Shot {
  /** Index of the pooled mesh (the concept takes the first hidden one, L985). */
  slot: number;
  kind: ShotKind;
  cause: CrashCause;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  prevX: number;
  prevY: number;
  prevZ: number;
  /** Seconds to the aim point; tracers fly on for `over` seconds past it. */
  life: number;
  over: number;
  /** The autopilot notices this shot and sidesteps it (concept L993, L1169–L1174). */
  dodge: boolean;
}

export interface Tracer {
  life: number;
  visible: boolean;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  prevX: number;
  prevY: number;
  prevZ: number;
}

export interface BridgeSeg {
  /** Rest position, local to the bridge root. */
  homeX: number;
  homeY: number;
  homeZ: number;
  x: number;
  y: number;
  z: number;
  rotX: number;
  rotY: number;
  rotZ: number;
  visible: boolean;
  /** Falls under gravity once the bridge breaks here (concept `userData.v`). */
  falling: boolean;
  vx: number;
  vy: number;
  vz: number;
  wx: number;
  wy: number;
  wz: number;
  /** ±1 for a neighbouring span that sags, 0 otherwise. */
  sag: number;
  splashed: boolean;
}

export interface Turret {
  seg: number;
  visible: boolean;
  headY: number;
  flash: boolean;
  cd: number;
}

export interface BridgeState {
  rootX: number;
  /** The bridge line; it stays at the profile's bridge z and is reset at every world shift (concept L1214). */
  z: number;
  deckY: number;
  span: number;
  /** Span length and count. */
  seg: number;
  n: number;
  intact: boolean;
  /** The guns are tracking the jet: they may fire this step (the HUD's alert, D18). */
  engaged: boolean;
  hp: number;
  /** Seconds since it broke. */
  since: number;
  segs: BridgeSeg[];
  turrets: Turret[];
  /** Spans that fall or sag, in the order the concept animates them (concept `B.fall`). */
  fall: number[];
  fallCount: number;
}

/** Events for the HUD's banners (GDD §7.3, M5), kept in a fixed ring. */
export type SimEventType = 'levelStart' | 'bridgeDestroyed' | 'extraJet' | 'jetLost' | 'gameOver' | 'respawn';
export interface SimEvent {
  type: SimEventType;
  /** Level for 'levelStart', cause for 'jetLost'. */
  level: number;
  cause: CrashCause | null;
}

/** An ordered list over preallocated items: removal compacts in place, so order is kept and nothing is created. */
export interface List<T> {
  items: T[];
  count: number;
}

export function createList<T>(capacity: number, fill: T): List<T> {
  return { items: new Array<T>(capacity).fill(fill), count: 0 };
}

export function listPush<T>(l: List<T>, item: T): void {
  if (l.count >= l.items.length) throw new Error(`list capacity ${l.items.length} exceeded`);
  l.items[l.count++] = item;
}

export function listRemoveAt<T>(l: List<T>, i: number): void {
  const it = l.items;
  for (let k = i; k < l.count - 1; k++) it[k] = it[k + 1] as T;
  l.count--;
}

export function createEnemy(type: EnemyType, slot: number): Enemy {
  return {
    type,
    slot,
    x: 0,
    y: 0,
    z: 0,
    vx: 0,
    vy: 0,
    move: false,
    near: false,
    fire: false,
    dying: 0,
    spd: 0,
    hw: 0,
    hl: 0,
    cd: 0,
    face: 1,
    yaw: 0,
    ph: 0,
    px: 0,
    posX: 0,
    posY: 0,
    posZ: 0,
    rotX: 0,
    rotY: 0,
    rotZ: 0,
    prevX: 0,
    prevY: 0,
    prevZ: 0,
    posed: false,
    radar: 0,
    wake: false,
    // A model's lights are on until its first update (three.js meshes start visible).
    light: true,
  };
}

export function createShot(slot: number): Shot {
  return {
    slot,
    kind: 'tracer',
    cause: 'heliFire',
    x: 0,
    y: 0,
    z: 0,
    vx: 0,
    vy: 0,
    vz: 0,
    prevX: 0,
    prevY: 0,
    prevZ: 0,
    life: 0,
    over: 0,
    dodge: false,
  };
}

export function createTracer(): Tracer {
  return { life: 0, visible: false, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, prevX: 0, prevY: 0, prevZ: 0 };
}
